// @ts-check

if (!customElements.get("ff-collapsible-tabs")) {
  class FfCollapsibleTabs extends HTMLElement {
    /**
     * @type {"own_close" | "own_expand" | "else" | undefined}
     */
    ffTransition;
    /**
     * @type {undefined | function}
     */
    ffWaitingAction;
    /**
     * @type {boolean}
     * When true, height is calculated on click instead of on init (useful for hidden elements)
     */
    ffDynamicHeight = false;
    /**
     * @type {boolean}
     * When true, ffInit() must be called manually (useful when content is loaded dynamically)
     */
    ffManualInit = false;

    /**
     * @type {{tabTriggerEL: undefined | HTMLElement & {ffOnClick: () => void}, tabContentEL: undefined | HTMLElement}}
     */
    ffActive = {
      tabTriggerEL: undefined,
      tabContentEL: undefined,
    };

    constructor() {
      super();
      // Check if dynamic height mode is enabled
      this.ffDynamicHeight = this.dataset.ffDynamicHeight !== undefined;
      // Check if manual init mode is enabled (skip auto-init)
      this.ffManualInit = this.dataset.ffManualInit !== undefined;
      
      if (!this.ffManualInit) {
        this.ffInit();
      }
      
      const customHeight = this.dataset.ffCustomHeight;
      if (customHeight) {
        this.style.height = customHeight + "px";
      }
    }

    ffInit = () => {
      const tabsEL = [];
      /**
       * @type {HTMLElement[]}
       */
      const tabsContentEL = [];
      /**
       * @type {HTMLElement[]}
       */
      const tabsTriggersEL = [];

      // Find all triggers with class ff-collapsible-tab-trigger
      const triggers = this.querySelectorAll('.ff-collapsible-tab-trigger');

      triggers.forEach((tabTriggerEL) => {
        // @ts-ignore - Skip if already initialized (prevents duplicate listeners)
        if (tabTriggerEL._ffInitialized) return;
        
        // @ts-ignore
        const contentId = tabTriggerEL.dataset.ffContentId;
        // @ts-ignore
        const isActive = tabTriggerEL.dataset.ffActive === 'true';

        if (!contentId) return;

        const tabContentEL = this.querySelector(`#${contentId}`);

        if (!tabContentEL) return;
        
        // @ts-ignore - Mark as initialized
        tabTriggerEL._ffInitialized = true;

        // Find closest ff-collapsible-tab-wrapper to add active class
        // @ts-ignore
        const tabBlockEL = tabContentEL.closest('.ff-collapsible-tab-wrapper');

        // @ts-ignore
        tabsTriggersEL.push(tabTriggerEL);
        // @ts-ignore
        tabsContentEL.push(tabContentEL);

        if (isActive && tabBlockEL) {
          this.ffActive = {
            // @ts-ignore
            tabTriggerEL,
            // @ts-ignore
            tabContentEL,
            // @ts-ignore
            tabBlockEL,
          };
        } else if (isActive) {
          this.ffActive = {
            // @ts-ignore
            tabTriggerEL,
            // @ts-ignore
            tabContentEL,
          };
        }
      });

      tabsEL.push(...tabsContentEL, ...tabsTriggersEL);

      for (let index = 0; tabsTriggersEL.length > index; index++) {
        const tabContentEL = tabsContentEL[index];
        /**
         * @type {HTMLElement & {ffOnClick: () => void}}
         */
        // @ts-ignore
        const tabTriggerEL = tabsTriggersEL[index];
        // @ts-ignore
        const tabBlockEL = tabContentEL.closest('.ff-collapsible-tab-wrapper');

        tabTriggerEL.ffOnClick = () => {
          const isOpening = !tabTriggerEL.classList.contains("ff-collapsible-tab-active");
          
          // Dynamic height mode: calculate and apply height with JS
          if (this.ffDynamicHeight) {
            // Close all other content elements
            tabsContentEL.forEach((content) => {
              if (content !== tabContentEL) {
                // @ts-ignore
                content.style.height = '0px';
              }
            });
            
            if (isOpening) {
              // Calculate height from first child (scrollHeight works even if parent was hidden)
              // @ts-ignore
              const targetHeight = tabContentEL.children[0]?.scrollHeight || 0;
              // @ts-ignore
              tabContentEL.style.height = targetHeight + 'px';
            } else {
              // @ts-ignore
              tabContentEL.style.height = '0px';
            }
          }
          
          // Remove active class from all tab blocks
          const allTabBlocks = this.querySelectorAll('.ff-collapsible-tab-wrapper');
          allTabBlocks.forEach(block => {
            if (block !== tabBlockEL) {
              block.classList.remove("ff-collapsible-tab-active");
            }
          });

          for (let tabEl of tabsEL) {
            if (tabEl === tabTriggerEL) {
              continue;
            }
            if (tabEl === tabContentEL) {
              continue;
            }
            tabEl.classList.remove("ff-collapsible-tab-active");
          }

          tabTriggerEL.classList.toggle("ff-collapsible-tab-active");
          tabContentEL.classList.toggle("ff-collapsible-tab-active");
          if (tabBlockEL) {
            tabBlockEL.classList.toggle("ff-collapsible-tab-active");
          }
          tabTriggerEL.classList.add("ff-collapsible-tab_transitioning");

          if (this.ffActive.tabTriggerEL === tabTriggerEL) {
            this.ffTransition = "own_close";
          } else if (this.ffActive.tabTriggerEL === undefined) {
            this.ffTransition = "own_expand";
          } else {
            this.ffTransition = "else";
          }

          this.ffActive = {
            tabTriggerEL,
            tabContentEL,
            // @ts-ignore
            tabBlockEL,
          };
        };

        tabTriggerEL.addEventListener("click", (e) => {
          // Block clicks on the same trigger during any transition
          if (this.ffTransition !== undefined && tabTriggerEL === this.ffActive.tabTriggerEL) {
            return;
          }

          if (this.ffWaitingAction === undefined) {
            switch (this.ffTransition) {
              case "own_close":
                if (this.ffActive.tabTriggerEL != tabTriggerEL) {
                  this.ffWaitingAction = tabTriggerEL.ffOnClick;
                }
                break;

              case "own_expand":
                // Click on different trigger while one is expanding
                this.ffWaitingAction = () => {
                  this.ffActive.tabTriggerEL?.ffOnClick();
                  this.ffWaitingAction = tabTriggerEL.ffOnClick;
                };
                break;

              case "else":
                this.ffWaitingAction = tabTriggerEL.ffOnClick;
                break;

              default:
                if (
                  this.ffActive.tabTriggerEL &&
                  this.ffActive.tabTriggerEL != tabTriggerEL
                ) {
                  this.ffActive.tabTriggerEL.ffOnClick();
                  this.ffWaitingAction = tabTriggerEL.ffOnClick;
                } else {
                  tabTriggerEL.ffOnClick();
                }
                break;
            }
          }
        });

        // Only pre-calculate heights if NOT in dynamic mode
        if (!this.ffDynamicHeight) {
          const tabContentHeight = tabContentEL.children[0].clientHeight;
          tabContentEL.style.setProperty(
            "--ff-expanded-height",
            tabContentHeight + "px"
          );

          tabContentEL.style.setProperty(
            "--ff-collapsible-tabs-transition-seconds",
            tabContentHeight * 0.0004 + 0.04 + "s"
          );
        } else {
          // In dynamic mode, check if tab is initially active
          const isInitiallyActive = tabContentEL.classList.contains("ff-collapsible-tab-active");
          const targetHeight = tabContentEL.children[0]?.scrollHeight || 0;
          
          if (isInitiallyActive) tabContentEL.style.height = targetHeight + 'px';
          else tabContentEL.style.height = '0px';

          tabContentEL.style.setProperty(
            "--ff-collapsible-tabs-transition-seconds",
            targetHeight * 0.0004 + 0.04 + "s"
          );
        }

        tabContentEL.addEventListener("transitionend", () => {
          tabTriggerEL.classList.remove("ff-collapsible-tab_transitioning");

          if (this.ffTransition == "own_close") {
            this.ffActive = {
              tabTriggerEL: undefined,
              tabContentEL: undefined,
            };
          }

          if (this.ffWaitingAction) {
            //action has the ability to stablish a waiting action
            const action = this.ffWaitingAction;
            this.ffWaitingAction = undefined;
            action();
          } else {
            this.ffTransition = undefined;
          }
        });
      }

      // Responsive open: close tabs that shouldn't be open at the current breakpoint
      const bp = parseInt(this.dataset.ffBreakpoint) || 768;
      const isMobile = window.innerWidth <= bp;

      tabsTriggersEL.forEach((trigger, i) => {
        const tabBlock = tabsContentEL[i].closest('.ff-collapsible-tab-wrapper');
        if (!tabBlock) return;

        const openDk = tabBlock.dataset.ffOpenDk !== undefined;
        const openMb = tabBlock.dataset.ffOpenMb !== undefined;

        // Skip if both or neither (standard behavior)
        if (openDk === openMb) return;

        const shouldBeClosed = (openDk && isMobile) || (openMb && !isMobile);

        if (shouldBeClosed && trigger.classList.contains('ff-collapsible-tab-active')) {
          trigger.classList.remove('ff-collapsible-tab-active');
          tabsContentEL[i].classList.remove('ff-collapsible-tab-active');
          tabBlock.classList.remove('ff-collapsible-tab-active');

          if (this.ffDynamicHeight) {
            tabsContentEL[i].style.height = '0px';
          }

          if (this.ffActive.tabTriggerEL === trigger) {
            this.ffActive = { tabTriggerEL: undefined, tabContentEL: undefined };
          }
        }
      });

      this.classList.add('ff-collapsible-tabs--initialized');
    };
  }

  customElements.define("ff-collapsible-tabs", FfCollapsibleTabs);
}

// @ts-ignore
if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", () => {
    requestAnimationFrame(() => {
      /**
       * @type {NodeListOf<FfNavigationTabs>}
       */
      const navigators = document.querySelectorAll("ff-collapsible-tabs");
      navigators.forEach((tabs) => {
        tabs.ffInit();
      });
    });
  });
}
